import {createTheme, createVar, style} from "@vanilla-extract/css";
import {calc} from "@vanilla-extract/css-utils";
import {hoverGroupContainer, transitions} from "../../css/decoration.css";
import {colorThemeVars, elementThemeVars} from "../../css/themes/themes.css";
import {applyThemeWithinVars} from "../../css/themes/theme-utils";
import dsStyles from "../../css/index.css";
import {iconStyles} from "../DSIcon/DSIcon.css";

const vars = {
  px: createVar(),
  contentPaddingVal: createVar(),
  missingBorderPadding: createVar(),
  plContent: createVar(),
  prContent: createVar(),
  hoverAlpha: createVar(),
  activeAlpha: createVar(),
};

export const buttonVars = vars;

const loadingStyle = style({});
const disabledLink = style({});

const baseStyles = style([
  transitions.colors,
  dsStyles.focusRing.true,
  {
    appearance: "none",
    cursor: "pointer",
    userSelect: "none",
    textDecoration: "none",
    overflow: "hidden",
    position: "relative",
    flex: "none",
    ":disabled": {cursor: "default"},
    selectors: {
      [`&${disabledLink}`]: {cursor: "default"},
    },

    vars: {
      [vars.plContent]: "0px",
      [vars.prContent]: "0px",
      [vars.contentPaddingVal]: "0px",
      [vars.missingBorderPadding]: "0px",
    },
    paddingRight: calc(vars.px).add(vars.prContent).add(vars.missingBorderPadding).toString(),
    paddingLeft: calc(vars.px).add(vars.plContent).add(vars.missingBorderPadding).toString(),
  },
]);

export const buttonBaseStyles = baseStyles;

const [primaryTheme, primaryThemeVars] = createTheme({
  base: {
    surface: elementThemeVars.main.base,
    textPrimary: elementThemeVars.contrast.base,
  },
  hover: {
    surface: elementThemeVars.main.lighter,
    textPrimary: elementThemeVars.contrast.lighter,
  },
  active: {
    surface: elementThemeVars.main.darker,
    textPrimary: elementThemeVars.contrast.darker,
  },
});
const [primaryBaseTheme, primaryBaseThemeVars] = createTheme(primaryThemeVars.base);

const [secondaryTheme, secondaryThemeVars] = createTheme({
  base: {
    surface: "transparent",
  },
  hover: {
    surface: `light-dark(rgb(from ${elementThemeVars.main.base} r g b / .16), rgb(from ${elementThemeVars.main.base} r g b / .48))`,
  },
  active: {
    surface: `light-dark(rgb(from ${elementThemeVars.main.base} r g b / .36), rgb(from ${elementThemeVars.main.base} r g b / .24))`,
  },
});
const [secondaryBaseTheme, secondaryBaseThemeVars] = createTheme(secondaryThemeVars.base);

const subtleTheme = createTheme(secondaryThemeVars, {
  base: {
    surface: elementThemeVars.subtle.base,
  },
  hover: {
    surface: elementThemeVars.subtle.lighter,
  },
  active: {
    surface: elementThemeVars.subtle.darker,
  },
});

const baseTransparent = style({
  backgroundColor: secondaryBaseThemeVars.surface,
  color: elementThemeVars.contrast.onTransparent,
  selectors: {
    [`&:hover:not(:disabled,${disabledLink})`]: {
      vars: applyThemeWithinVars(secondaryBaseThemeVars, secondaryThemeVars.hover),
    },
    [`&:active:not(:disabled,${disabledLink})`]: {
      vars: applyThemeWithinVars(secondaryBaseThemeVars, secondaryThemeVars.active),
    },
    [`${hoverGroupContainer}:hover &:not(:disabled,${disabledLink})`]: {
      vars: applyThemeWithinVars(secondaryBaseThemeVars, secondaryThemeVars.hover),
    },
  },
});

const baseSubtle = style({
  backgroundColor: secondaryBaseThemeVars.surface,
  color: elementThemeVars.contrast.subtle,
  selectors: {
    [`&:hover:not(:disabled,${disabledLink})`]: {
      vars: applyThemeWithinVars(secondaryBaseThemeVars, secondaryThemeVars.hover),
    },
    [`&:active:not(:disabled,${disabledLink})`]: {
      vars: applyThemeWithinVars(secondaryBaseThemeVars, secondaryThemeVars.active),
    },
    [`${hoverGroupContainer}:hover &:not(:disabled,${disabledLink})`]: {
      vars: applyThemeWithinVars(secondaryBaseThemeVars, secondaryThemeVars.hover),
    },
  },
  // for spinner
  vars: {
    [colorThemeVars.border.default]: elementThemeVars.subtle.darker,
    [colorThemeVars.text.primary]: elementThemeVars.contrast.subtle,
    [colorThemeVars.text.secondary]: elementThemeVars.contrast.subtle,
  },
});

const primaryBaseStyle = style({
  backgroundColor: primaryBaseThemeVars.surface,
  color: primaryBaseThemeVars.textPrimary,
  selectors: {
    [`&:hover:not(:disabled,${disabledLink})`]: {
      vars: applyThemeWithinVars(primaryBaseThemeVars, primaryThemeVars.hover),
    },
    [`&:active:not(:disabled,${disabledLink})`]: {
      vars: applyThemeWithinVars(primaryBaseThemeVars, primaryThemeVars.active),
    },
    [`${hoverGroupContainer}:hover &:not(:disabled,${disabledLink})`]: {
      vars: applyThemeWithinVars(primaryBaseThemeVars, primaryThemeVars.hover),
    },
  },
  // for spinner
  vars: {
    [colorThemeVars.border.default]: elementThemeVars.border.base,
    [colorThemeVars.text.primary]: elementThemeVars.contrast.base,
  },
});

const noBorderStyle = style({border: "none", vars: {[vars.missingBorderPadding]: "1px"}});

const hoverIconOverlay = style([
  transitions.colors,
  {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: elementThemeVars.main.base,
    color: elementThemeVars.contrast.base,
    opacity: 0,
    pointerEvents: "none",
    selectors: {
      ":hover:not(:disabled) > &": {opacity: 1},
    },
  },
]);

export const buttonStyles = {
  layout: {
    default: style([dsStyles.display.block]),
    centered: style({display: "flex", alignItems: "center", justifyContent: "center"}),
  },
  disabledType: {
    default: style({
      selectors: {
        [`&:disabled::before, &${disabledLink}::before`]: {
          content: '""',
          position: "absolute",
          inset: 0,
          backgroundColor: `light-dark(#575E75, #494B75)`,
          opacity: 0.25,
          mixBlendMode: "saturation",
        },
        [`&:disabled:not(${loadingStyle}), &${disabledLink}:not(${loadingStyle})`]: {
          opacity: 0.5,
        },
      },
    }),
    justOpacity: style({
      selectors: {
        [`&:disabled:not(${loadingStyle}), &${disabledLink}:not(${loadingStyle})`]: {
          opacity: 0.5,
        },
      },
    }),
  },
  variant: {
    primary: style([baseStyles, primaryTheme, primaryBaseTheme, primaryBaseStyle, noBorderStyle]),
    secondary: style([
      baseStyles,
      secondaryTheme,
      secondaryBaseTheme,
      baseTransparent,
      {border: `1px solid ${elementThemeVars.border.base}`},
    ]),
    tertiary: style([
      baseStyles,
      secondaryTheme,
      secondaryBaseTheme,
      baseTransparent,
      noBorderStyle,
    ]),
  },
  subtleVariants: {
    secondary: style([
      baseStyles,
      subtleTheme,
      secondaryBaseTheme,
      baseSubtle,
      {border: `1px solid ${elementThemeVars.border.base}`},
    ]),
    tertiary: style([baseStyles, subtleTheme, secondaryBaseTheme, baseSubtle, noBorderStyle]),
  },
  size: {
    sm: style([
      dsStyles.textType.label12,
      iconStyles.sizes[16],
      {
        height: 24,
        borderRadius: 4,
        vars: {[vars.px]: "3px", [vars.contentPaddingVal]: "4px"},
      },
    ]),
    md: style([
      dsStyles.textType.label14,
      iconStyles.sizes[20],
      {
        height: 32,
        borderRadius: 4,
        vars: {[vars.px]: "5px", [vars.contentPaddingVal]: "4px"},
      },
    ]),
    "~md_28": style([
      dsStyles.textType.label14,
      iconStyles.sizes[20],
      {
        height: 28,
        borderRadius: 4,
        vars: {[vars.px]: "3px", [vars.contentPaddingVal]: "4px"},
      },
    ]),
    lg: style([
      dsStyles.textType.label16,
      iconStyles.sizes[24],
      {
        height: 40,
        borderRadius: 4,
        vars: {[vars.px]: "7px", [vars.contentPaddingVal]: "4px"},
      },
    ]),
    xl: style([
      dsStyles.textType.title18,
      iconStyles.sizes[24],
      {
        height: 48,
        borderRadius: 4,
        vars: {[vars.px]: "11px", [vars.contentPaddingVal]: "4px"},
      },
    ]),
    inline: style([
      dsStyles.textType.label16,
      iconStyles.sizes[20],
      {
        height: 40,
        borderRadius: 4,
        vars: {[vars.px]: "7px", [vars.contentPaddingVal]: "4px"},
      },
    ]),
  },
  contentPaddings: {
    both: style({
      vars: {
        [vars.plContent]: vars.contentPaddingVal,
        [vars.prContent]: vars.contentPaddingVal,
      },
    }),
    left: style({
      vars: {
        [vars.plContent]: vars.contentPaddingVal,
      },
    }),
    right: style({
      vars: {
        [vars.prContent]: vars.contentPaddingVal,
      },
    }),
  },
  negatePadding: style({
    marginRight: calc(vars.px).add(vars.prContent).negate().toString(),
    marginLeft: calc(vars.px).add(vars.plContent).negate().toString(),
    marginTop: calc(vars.px).negate().toString(),
    marginBottom: calc(vars.px).negate().toString(),
  }),
  loadingStyle,
  disabledLink,
  hoverIconOverlay,
  solidBackgroundColor: style({
    backgroundColor: `color-mix(in srgb, ${colorThemeVars.bg} 75%, transparent)`,
    backdropFilter: "blur(10px)",
    selectors: {
      [`&:hover:not(:disabled,${disabledLink})`]: {
        backgroundColor: `color-mix(in srgb, ${colorThemeVars.table.alternatingRowBg} 50%, transparent)`,
      },
      [`&:active:not(:disabled,${disabledLink})`]: {
        backgroundColor: `color-mix(in srgb, ${colorThemeVars.bg} 90%, transparent)`,
      },
    },
  }),
};
